import type { MatchScales } from "./types";

export type ScaleAxis = keyof MatchScales;

/**
 * Values-score weights (must sum to 1).
 * Commitment 30% · Honesty 25% · Loyalty 25% · Passion 20%
 */
export const VALUE_WEIGHTS: Record<ScaleAxis, number> = {
  commitment: 0.3,
  honesty: 0.25,
  loyalty: 0.25,
  passion: 0.2,
};

export const SCALE_AXES: ScaleAxis[] = [
  "loyalty",
  "honesty",
  "passion",
  "commitment",
];

/**
 * Weekly curated selection (Kirk 2026-08-21-D).
 * Cap is fixed at 4 — never pad; short weeks show fewer with honest copy.
 */
export const WEEKLY_SUGGESTION_CAP = 4;
export const WEEKLY_SUGGESTION_MIN = 1;
export const WEEKLY_SUGGESTION_MAX = 4;
export const WEEKLY_SUGGESTION_DEFAULT = 4;

/** Pass is eligible for replacement for this long (when pool allows). */
export const PASS_REPLACE_MS = 24 * 60 * 60 * 1000;

/**
 * Weekly heart budget (Masco 2026-08-29). One place, exported — never a
 * literal in a component. Hearts are the scarce unit of interest; credits buy
 * new people. Two economies, kept apart on purpose.
 */
export const HEARTS_PER_WEEK_FREE = 2;
/** Guest demo weekly hearts — Kirk 2026-09-12: two, not the subscriber five. */
export const HEARTS_PER_WEEK_GUEST = 2;
export const HEARTS_PER_WEEK_SUBSCRIBER = 5;

/** Hearts a plan gets each ISO week. `null` means unlimited (VIP). */
export function heartsPerWeek(
  plan: "free" | "trial" | "subscriber" | "vip"
): number | null {
  if (plan === "vip") return null;
  if (plan === "subscriber" || plan === "trial") return HEARTS_PER_WEEK_SUBSCRIBER;
  return HEARTS_PER_WEEK_FREE;
}

/** Guest demo IDs (`user_guest_*`) override to subscriber budget in MatchFeed — see AGENTS.md. */

/** Flame = superlike. Scarce on purpose. Free/sub 1 a week; VIP unlimited. */
export const FLAMES_PER_WEEK = 1;

export function flamesPerWeek(
  plan: "free" | "trial" | "subscriber" | "vip"
): number | null {
  if (plan === "vip") return null;
  return FLAMES_PER_WEEK;
}

/**
 * Short note on a like or flame. Enforced here, not only in the input.
 * 33 letters (Masco 2026-09-13) — was 20, before that ten words.
 */
export const COMMENT_MAX_LETTERS = 33;

/** @deprecated Use COMMENT_MAX_LETTERS. */
export const COMMENT_MAX_WORDS = COMMENT_MAX_LETTERS;

/** Trimmed character count. */
export function countLetters(s: string): number {
  return s.trim().length;
}

/** Words = whitespace-separated tokens after trimming. "Psalm 34:8" is one. */
export function countWords(s: string): number {
  const trimmed = s.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

/** A comment is valid when it has 1–33 letters. */
export function isCommentValid(s: string): boolean {
  const n = countLetters(s);
  return n >= 1 && n <= COMMENT_MAX_LETTERS;
}

/** @deprecated Use WEEKLY_* — kept as aliases for older call sites. */
export const DAILY_SUGGESTION_MIN = WEEKLY_SUGGESTION_MIN;
export const DAILY_SUGGESTION_MAX = WEEKLY_SUGGESTION_MAX;
export const DAILY_SUGGESTION_DEFAULT = WEEKLY_SUGGESTION_DEFAULT;

export function clampWeeklyLimit(n: number): number {
  const rounded = Math.round(n);
  if (Number.isNaN(rounded)) return WEEKLY_SUGGESTION_DEFAULT;
  return Math.min(WEEKLY_SUGGESTION_MAX, Math.max(WEEKLY_SUGGESTION_MIN, rounded));
}

/** @deprecated alias */
export function clampDailyLimit(n: number): number {
  return clampWeeklyLimit(n);
}

/** Below this, the weekly algorithm does not show the person. */
export const MIN_SHOWN_SCORE = 50;

export type ScoreBand = "green" | "yellow" | "orange" | "red";

/** 90+ green · 80+ yellow · 70+ orange · 60+ (and 50–59) red. */
export function scoreBand(score: number): ScoreBand | null {
  if (score < MIN_SHOWN_SCORE) return null;
  if (score >= 90) return "green";
  if (score >= 80) return "yellow";
  if (score >= 70) return "orange";
  return "red";
}
